import { isFunction } from './function';

interface BlobUrlSource {
  src?: string;
  downloadSrc?: string;
  thumbnail?: string;
}

type IncludeSrcOption<T extends BlobUrlSource> =
  | boolean
  | ((file: T) => boolean);

type IncludeDownloadSrcOption<T extends BlobUrlSource> =
  | boolean
  | ((file: T) => boolean);

type IncludeThumbnailOption<T extends BlobUrlSource> =
  | boolean
  | ((file: T) => boolean);

interface CollectBlobUrlsOptions<T extends BlobUrlSource> {
  includeSrc?: IncludeSrcOption<T>;
  includeDownloadSrc?: IncludeDownloadSrcOption<T>;
  includeThumbnail?: IncludeThumbnailOption<T>;
}

const BLOB_URL_PREFIX = 'blob:';

export function isBlobUrl(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith(BLOB_URL_PREFIX);
}

export function collectBlobUrls<T extends BlobUrlSource>(
  file: T,
  options: CollectBlobUrlsOptions<T> = {}
): Set<string> {
  const {
    includeSrc = true,
    includeDownloadSrc = true,
    includeThumbnail = true,
  } = options;
  const shouldIncludeSrc =
    isFunction<(file: T) => boolean>(includeSrc)
      ? includeSrc(file)
      : includeSrc;
  const shouldIncludeDownloadSrc =
    isFunction<(file: T) => boolean>(includeDownloadSrc)
      ? includeDownloadSrc(file)
      : includeDownloadSrc;
  const shouldIncludeThumbnail =
    isFunction<(file: T) => boolean>(includeThumbnail)
      ? includeThumbnail(file)
      : includeThumbnail;
  const urls = new Set<string>();

  if (shouldIncludeSrc && isBlobUrl(file.src)) {
    urls.add(file.src);
  }

  if (shouldIncludeDownloadSrc && isBlobUrl(file.downloadSrc)) {
    urls.add(file.downloadSrc);
  }

  if (shouldIncludeThumbnail && isBlobUrl(file.thumbnail)) {
    urls.add(file.thumbnail);
  }

  return urls;
}

export function revokeBlobUrls(urls: Iterable<string>): void {
  Array.from(new Set(urls)).forEach((url) => {
    URL.revokeObjectURL(url);
  });
}

export function revokeBlobUrlsSafely(urls: Iterable<string>): void {
  Array.from(new Set(urls)).forEach((url) => {
    if (!isBlobUrl(url)) {
      return;
    }

    try {
      URL.revokeObjectURL(url);
    } catch {
      // Keep caller flow stable even if browser revoke fails.
    }
  });
}

export function revokeRemovedBlobUrls<T extends BlobUrlSource>(
  previousFiles: T[],
  currentFiles: T[],
  options: CollectBlobUrlsOptions<T> = {}
): void {
  const previousBlobUrls = new Set<string>();
  previousFiles.forEach((file) => {
    collectBlobUrls(file, options).forEach((url) => {
      previousBlobUrls.add(url);
    });
  });

  const currentBlobUrls = new Set<string>();
  currentFiles.forEach((file) => {
    collectBlobUrls(file, options).forEach((url) => {
      currentBlobUrls.add(url);
    });
  });

  const removedBlobUrls: string[] = [];
  previousBlobUrls.forEach((url) => {
    if (!currentBlobUrls.has(url)) {
      removedBlobUrls.push(url);
    }
  });

  if (removedBlobUrls.length > 0) {
    revokeBlobUrls(removedBlobUrls);
  }
}